// WhyNewZealandPR.js
import React from 'react';
import { motion } from 'framer-motion';
import { Globe, Heart, Briefcase, GraduationCap, Shield, TrendingUp } from 'lucide-react';

const WhyNewZealandPR = () => {
  const benefits = [
    {
      icon: Globe,
      title: 'Straightforward Immigration System',
      description: 'Clear points-based system with transparent criteria. New Zealand actively welcomes skilled migrants with streamlined application processes and regular selection draws.'
    },
    {
      icon: Heart,
      title: 'Outstanding Quality of Life',
      description: 'Stunning natural beauty, safe communities, excellent work-life balance, and consistently ranked among the world\'s best countries for quality of life and happiness.'
    },
    {
      icon: Briefcase,
      title: 'Growing Economy',
      description: 'Strong job market with demand for skilled workers in IT, healthcare, engineering, and trades. Competitive salaries and excellent worker protections.'
    },
    {
      icon: GraduationCap,
      title: 'Excellent Education System',
      description: 'World-class education system with free public schooling for PR holders\' children. New Zealand qualifications recognized internationally.'
    },
    {
      icon: Shield,
      title: 'Comprehensive Healthcare',
      description: 'Public healthcare system providing free or subsidized medical care for residents. Strong social support systems and safety nets.'
    },
    {
      icon: TrendingUp,
      title: 'Fast Track to Citizenship',
      description: 'Permanent residents can apply for citizenship after 5 years of residence. New Zealand allows dual citizenship.'
    }
  ];

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-semibold text-[#202356] mb-4">
            Why Choose New Zealand for Permanent Residency?
          </h2>
          <p className="text-gray-600 text-base font-light max-w-3xl mx-auto">
            New Zealand offers an attractive skilled migration program combining career opportunities with an exceptional lifestyle in one of the world's most beautiful countries.
          </p>
        </motion.div>

        {/* Benefits Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {benefits.map((benefit, index) => {
            const Icon = benefit.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-[#F5F8FA] p-6 rounded-lg hover:shadow-lg transition-shadow"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-[#202356] to-[#2542D4] rounded-lg flex items-center justify-center mb-4">
                  <Icon size={24} className="text-white" />
                </div>
                <h3 className="text-lg font-semibold text-[#202356] mb-3">
                  {benefit.title}
                </h3>
                <p className="text-gray-600 text-sm font-light leading-relaxed">
                  {benefit.description}
                </p>
              </motion.div>
            );
          })}
        </div>

        {/* Additional Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="mt-12 bg-blue-50 border border-blue-200 rounded-lg p-6"
        >
          <p className="text-gray-700 text-sm font-light leading-relaxed text-center">
            <strong>Did you know?</strong> New Zealand permanent residents have the right to live, work, and study anywhere in New Zealand indefinitely. You can also travel to and from New Zealand for up to 2 years on your resident visa.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default WhyNewZealandPR;
